import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { gridQuery, isNoGrid, type Envelope, type Market } from "./api";
import { ENTITY_LABEL } from "./config";
import { COPY, humanDate, MARKET_LABEL, messageFor, money, pct, stamp } from "./format";
import {
  foreignCurrency, HIGHLIGHT_IDS, hoursAgo, isKpiUnknown, KPI_COPY, kpiLabel, kpiName, kpiQuery, MARKETS, missingText, NS_SCOPE_LABEL,
  nsParamsFrom, PER_UNIT_IDS, regionsQuery, scopeKey, unitOf, valueLabel, type ForecastScope, type KpiId, type KpiRead, type RegionCard,
  type RegionsRead, type ScopeKey,
} from "./kpi";
import { BandChip, MoveNote } from "./MarginHealthSection";
import { ConfigText, RetryAlert, SectionHeading } from "./WeeklyMovesSection";

const T = KPI_COPY.highlights;
const WARN = "border-ks-warn text-ks-warn";

/**
 * Section 2 of the Overview (R5 slice 2; C1 v1.29): the three KPI tiles (k125 and k367 as this month's forecast on the scope
 * section 1 shows, k371 as the definition-only tile) and the three region cards (SKUs below the Config margin target in
 * today's grid on the default basis, the set C2 says it counted, the entity's Xero read, the prices set since that grid ran).
 * Read-only: the one control opens the Price Run with the region set.
 */
export function HighlightsSection({ areaName }: { areaName: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  // Section 1's scope (`pp_ns_region`) picks every tile's scope; the cards always read today's grid on the default basis,
  // the grid the Basis chips describe (v1.1: nothing on the Overview names another basis or date).
  const scope = nsParamsFrom(search).scope;
  const key: ScopeKey = scopeKey(scope);
  const regionLabel = key === "all" ? null : NS_SCOPE_LABEL[scope];
  const kpis = useQueries({ queries: HIGHLIGHT_IDS.map((id) => kpiQuery(id)) });
  const regions = useQuery(regionsQuery());
  const grids = useQueries({ queries: MARKETS.map((m) => gridQuery(m)) });
  // Keyed by market, never by position: the cards come in C2's order, the grid reads in MARKETS order.
  const gridOf = (market: Market) => grids[MARKETS.indexOf(market)];
  const open = (market: Market) => void navigate({ to: "/app/$area/$module/$tab", params: { area: "margin_engine", module: "precision_pricing", tab: "price_run" }, search: { pp_region: market.toLowerCase() } });
  // v1.2: each tile and the card block own their pending, error and empty states; nothing gates the section as a whole.
  // TanStack 5 puts a read that failed with no data back to pending on every refetch, so a section-wide gate blanked healthy
  // tiles on each focus while one KPI was not served yet (merge-review of PR 423). The cards never wait on the KPI reads:
  // C2's regions route does not read the KPI mirrors.
  let cards;
  if (regions.data) {
    // A read that holds data stays on screen when its latest refetch failed (the query keeps the data, v1.1).
    const data: RegionsRead = regions.data;
    cards = <>
      <div className="grid gap-4 md:grid-cols-3">
        {data.regions.map((card) => {
          const grid = gridOf(card.market);
          return <RegionCardView key={card.market} card={card} target={data.target} envelope={grid?.data?.envelope ?? null} noGrid={grid?.error != null && isNoGrid(grid.error)} inChart={key === card.market} onOpen={() => open(card.market)} />;
        })}
      </div>
      <p className="text-xs text-ks-grey-1"><ConfigText text={T.cardsFooter} /></p>
    </>;
  } else if (regions.error) {
    cards = <RetryAlert message={messageFor(regions.error, areaName)} onRetry={() => void regions.refetch()} />;
  } else {
    cards = <div className="grid gap-4 md:grid-cols-3">{MARKETS.map((m) => <Skeleton key={m} className="h-28 w-full" />)}</div>;
  }
  return (
    <section className="flex flex-col gap-3">
      <SectionHeading n={2} title={T.title} desc={T.desc} />
      <div className="grid gap-4 md:grid-cols-3">
        {kpis.map((q, i) => <TileSlot key={HIGHLIGHT_IDS[i]} id={HIGHLIGHT_IDS[i]} query={q} scopeKey={key} regionLabel={regionLabel} areaName={areaName} />)}
      </div>
      {cards}
    </section>
  );
}

/** One tile's own state (v1.2): the read when it holds data (a failed refetch keeps it), a skeleton while it loads, the
 * not-available tile on C2's `unknown_kpi`, any other refusal in this tile alone. Every state names the KPI (CLB-015). */
function TileSlot({ id, query, scopeKey: key, regionLabel, areaName }: { id: KpiId; query: UseQueryResult<KpiRead>; scopeKey: ScopeKey; regionLabel: string | null; areaName: string }) {
  if (query.data) return <KpiTile read={query.data} scopeKey={key} regionLabel={regionLabel} />;
  if (query.error) {
    const unknown = isKpiUnknown(query.error);
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col gap-1 py-4">
          <div className="text-sm font-semibold text-ks-ink">{unknown ? T.notServed(kpiLabel(id)) : kpiLabel(id)}</div>
          {unknown
            ? <div className="text-xs text-ks-grey-1">{T.notServedWhy}</div>
            : <>
                <p role="alert" className="text-xs text-ks-ink">{messageFor(query.error, areaName)}</p>
                <Button size="sm" variant="outline" className="w-fit" data-nonmutating data-retry onClick={() => void query.refetch()}>{COPY.retry}</Button>
              </>}
        </CardContent>
      </Card>
    );
  }
  return <Skeleton className="h-28 w-full" />;
}

/** A KPI tile: name and id (CLB-015), this month's forecast on the scope with its band; a figure in a currency other than GBP
 * says why it has no band, any other figure without one carries the grey "no band" chip; the source line, with the per-unit
 * sentence on a per-unit KPI; a KPI with no registered calculation reads "No calculation yet" (k371 with its own note).
 * C1 v1.30: under the band, the staged batch's figure with its move against the committed one, or "Nothing staged" (C2 v1.33;
 * nothing on an older API). */
function KpiTile({ read, scopeKey: key, regionLabel }: { read: KpiRead; scopeKey: ScopeKey; regionLabel: string | null }) {
  const scope: ForecastScope | null = read.forecast.scopes[key] ?? null;
  const definitionOnly = read.forecast.missing.includes("no_registered_calculation");
  const unit = unitOf(scope);
  const perUnit = PER_UNIT_IDS.includes(read.kpi.id as KpiId);
  return (
    <Card className={definitionOnly ? "border-dashed" : undefined}>
      <CardContent className="flex flex-col gap-1 py-4">
        <div className="text-sm font-semibold text-ks-ink">{kpiName(read.kpi)}</div>
        {definitionOnly
          ? <><Badge variant="outline" className="w-fit">{T.noCalcYet}</Badge>{read.kpi.id === "k371" && <div className="text-xs text-ks-grey-1">{T.k371Note(read.kpi.status)}</div>}</>
          : scope?.value != null
            ? <>
                <div className="text-2xl font-semibold tabular-nums text-ks-ink">{valueLabel(scope.value, unit)}</div>
                {scope.band == null && foreignCurrency(unit) ? <div className="text-xs text-ks-grey-1">{T.noBandCurrency}</div> : <BandChip band={scope.band} />}
                {scope.staged && (scope.staged.saved === 0
                  ? <Badge variant="outline" className="w-fit text-ks-grey-1">{T.nothingStaged}</Badge>
                  : <div className="text-xs text-ks-ink" data-staged>{scope.staged.value != null
                      ? <><span className="font-semibold">{T.staged} {valueLabel(scope.staged.value, unit)}</span>{scope.staged.delta != null && " · "}<MoveNote prefix="" delta={scope.staged.delta} unit={unit} orientation={read.orientation} /></>
                      : KPI_COPY.health.noStagedFigure(scope.staged.missing)}</div>)}
                <div className="text-xs text-ks-grey-1">{regionLabel ? T.regionSource(regionLabel, read.forecast.base_month) : T.tileSource(read.forecast.base_month)}{perUnit ? ` ${T.perUnit}` : ""}</div>
              </>
            : <><div className="text-sm text-ks-ink">{KPI_COPY.health.noCalculation}</div><div className="text-xs text-ks-grey-1">{missingText(scope?.missing.length ? scope.missing : read.forecast.missing)}</div></>}
        {/* T28 (audit O2, UX 9.1, H26): a KPI that is not LIVE carries its status, as on the Margin health card. */}
        {read.kpi.status && read.kpi.status !== "LIVE" && <Badge variant="outline" className="w-fit" data-kpi-status>{KPI_COPY.health.status(read.kpi.status)}</Badge>}
      </CardContent>
    </Card>
  );
}

/** One region card: the market, "In the chart" with the purple border on the region section 1 shows (v1.2: no dim on the
 * others), the Basis chip from today's grid envelope ("no grid yet" only when C2 says so, nothing while it loads), the Xero
 * read chip in the Price Run's words, the entity and currency, the set counted (only when a set was read), the big
 * below-target count, the grid-computed line, the Price Run button. */
function RegionCardView({ card, target, envelope, noGrid, inChart, onOpen }: { card: RegionCard; target: RegionsRead["target"]; envelope: Envelope | null; noGrid: boolean; inChart: boolean; onOpen: () => void }) {
  let big;
  if (card.missing.includes("no_grid")) big = <p className="text-sm text-ks-ink">{T.noGrid}</p>;
  else if (card.missing.includes("grid_unavailable")) big = <p className="text-sm text-ks-ink">{T.gridUnavailable}</p>;
  else if (card.rows_priced === 0) big = <p className="text-sm text-ks-ink"><b>{T.noPriced}</b><br /><span className="text-ks-grey-1">{T.noPricedWhy}</span></p>;
  else if (target.value == null || card.below_target == null) big = <div className="flex items-center gap-2"><span className="text-3xl font-semibold text-ks-grey-1">—</span><Badge variant="outline"><ConfigText text={target.not_set ?? ""} /></Badge></div>;
  else big = <><div className={`text-3xl font-semibold tabular-nums ${card.below_target > 0 ? "text-ks-warn" : "text-ks-pass"}`}>{card.below_target}</div><div className="text-xs text-ks-grey-1"><ConfigText text={T.below(target.value)} /></div></>;
  const now = new Date();
  // The set C2 says it counted: only when it read one (a run found but unreadable counted nothing) and named its basis.
  const countedBasis = card.grid_run_id != null && !card.missing.includes("grid_unavailable") ? card.basis : undefined;
  // The entity's own Xero read, in the Price Run's words (format.ts COPY), toned when C2's freshness says the weekly read is
  // older than expected (the envelope's `pool_cogs`, its own 8 days: owner ruling Q11); absent on an API before C2 v1.32.
  const readAt = card.pool_cogs_read_at;
  const oldRead = envelope?.feeds.pool_cogs?.stale === true;
  return (
    <Card className={inChart ? "border-ks-purple" : undefined} data-in-chart={inChart ? "true" : undefined}>
      <CardContent className="flex flex-col gap-2 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold text-ks-ink">{MARKET_LABEL[card.market]}</span>
          {inChart && <Badge className="bg-ks-purple text-white">{T.inChart}</Badge>}
          {envelope
            ? <Badge variant="outline" className={envelope.stale ? WARN : undefined}>{T.basis(hoursAgo(envelope.computed_at, now))}</Badge>
            : noGrid ? <Badge variant="outline">{T.basisNone}</Badge> : null}
          {readAt !== undefined && (readAt === null
            ? <Badge variant="outline" className={WARN}>{COPY.xeroNotRead}</Badge>
            : <Badge variant="outline" className={oldRead ? WARN : undefined}>{oldRead ? COPY.xeroReadOld(humanDate(readAt)) : COPY.xeroRead(humanDate(readAt))}</Badge>)}
        </div>
        <div className="text-xs text-ks-grey-1">{ENTITY_LABEL[card.entity_id] ?? card.entity_id} · {card.currency}</div>
        {countedBasis && <div className="text-xs text-ks-grey-1">{T.context(countedBasis, card.in_force_from)}</div>}
        {big}
        {card.computed_at && <div className="text-xs text-ks-grey-1">{T.gridComputed(stamp(card.computed_at), card.prices_set_this_run)}</div>}
        <SubscriptionLines block={card.subscription} />
        <Button size="sm" variant="outline" className="w-fit" data-nonmutating onClick={onOpen}>{T.openPriceRun}</Button>
      </CardContent>
    </Card>
  );
}

/** C1 v1.51 (c1-subscription-economics, CL-032): the two lines under the count, from C2 v1.53's block. Absent on an older
 *  API (one sentence), null before the first grid run writes a row (another), else the split line (or why there is no
 *  split) and the lifetime line, or the "Not enough history" chip in the engine's words with the count behind it. Every
 *  figure is the engine's string: `pct` draws a share as a whole percent (format.ts's own, as the Price Run), `money` formats the
 *  currency, nothing is worked out here (review round 1). */
function SubscriptionLines({ block }: { block: RegionCard["subscription"] }) {
  if (block === undefined) return <div className="text-xs text-ks-grey-1">{T.subscriptionNotServed}</div>;
  if (block === null) return <div className="text-xs text-ks-grey-1">{T.subscriptionNotComputed}</div>;
  const split = block.subscription_share_pct == null
    ? T.splitNone(block.window.weeks, block.window.end)
    : T.split(pct(block.subscription_share_pct), pct(block.single_share_pct), block.window.weeks, block.window.end);
  return (
    <div className="flex flex-col gap-1" data-subscription-economics>
      <div className="text-xs text-ks-ink">{split}{block.unmarked_orders > 0 && <span className="text-ks-grey-1"> · {T.splitUnmarked(block.unmarked_orders)}</span>}</div>
      {block.enough_history
        ? <div className="text-xs text-ks-ink">{T.lifetime(block.lifetime_orders_avg ?? "—", money(block.profit_yield_avg, block.currency), block.customers, block.book_since)}</div>
        : <div className="flex flex-wrap items-center gap-2"><Badge variant="outline">{block.not_enough_history ?? "Not enough history"}</Badge><span className="text-xs text-ks-grey-1">{T.lifetimeThin(block.customers, block.customers_min, block.book_since)}</span></div>}
    </div>
  );
}
